import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getHotTitles, searchTitles } from "@/lib/titles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TitleCard from "@/components/feature/TitleCard";
import Reveal from "@/components/base/Reveal";

const HOT_KEYWORDS = ["悬疑", "科幻", "古装", "青春", "治愈", "音乐"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);

  useSeo({
    title: keyword
      ? `${keyword} - 搜索结果 - WW我的快乐在哪里`
      : "影视搜索 - WW我的快乐在哪里",
    description: keyword
      ? `WW我的快乐在哪里中与“${keyword}”相关的影视资讯与剧情介绍搜索结果。`
      : "在 WW我的快乐在哪里 搜索电影、电视剧、综艺的资讯与剧情介绍。",
    keywords: "影视搜索,剧情搜索,WW我的快乐在哪里,影视资讯",
    path: "https://rozhryj.cn/search",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchTitles(keyword), [keyword]);
  const hotTitles = useMemo(() => getHotTitles(6), []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  return (
    <section className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-5">
        <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">影视搜索</h1>
        <p className="mt-3 text-sm text-foreground-600 md:text-base">
          输入关键词，支持在影视标题、剧情简介、导演、类型与标签中全文匹配。
        </p>
      </header>

      <form onSubmit={handleSubmit} className="mt-6" role="search">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex h-12 flex-1 items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              name="q"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：悬疑、科幻、治愈"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </div>
          <button
            type="submit"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-primary-600 px-8 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
          >
            <i className="ri-search-line" aria-hidden="true" />
            搜索
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {HOT_KEYWORDS.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => {
                setInput(word);
                setSearchParams({ q: word });
              }}
              className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
            >
              {word}
            </button>
          ))}
        </div>
      </form>

      {keyword ? (
        <div className="mt-8">
          <p className="text-sm text-foreground-600">
            “<span className="font-bold text-primary-600">{keyword}</span>” 的搜索结果，共
            <span className="font-bold text-primary-600"> {results.length} </span>条
          </p>

          {results.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((item, index) => (
                <Reveal key={item.id} delay={index * 50}>
                  <TitleCard item={item} className="h-full" />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
              </span>
              <h2 className="mt-5 text-lg font-bold text-foreground-950">没有找到相关影视作品</h2>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或看看下面这些热门影视内容。
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 px-4 py-12 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-search-line text-3xl" aria-hidden="true" />
          </span>
          <h2 className="mt-5 text-lg font-bold text-foreground-950">开始搜索你感兴趣的影视</h2>
          <p className="mt-2 text-sm text-foreground-600">可以试试“悬疑”“科幻”“治愈”等关键词。</p>
        </div>
      )}

      <div className="mt-12">
        <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
          <span className="h-5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
          热门影视推荐
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {hotTitles.map((item) => (
            <TitleCard key={item.id} item={item} className="h-full" />
          ))}
        </div>
      </div>

      <p className="mt-8 text-center text-xs text-foreground-500">
        没有找到想看的内容？
        <Link to="/about" className="ml-1 text-primary-600 hover:text-accent-600">
          告诉我们
        </Link>
      </p>
    </section>
  );
}